import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Truck, 
  Train, 
  Plane, 
  Ship, 
  ArrowRight, 
  MapPin, 
  Calendar,
  Package,
  Users,
  Building2,
  Percent
} from "lucide-react";

const transportModes = [
  {
    icon: Truck,
    name: "Road",
    description: "Trucks & Container Vehicles",
    image: "https://images.pexels.com/photos/5410923/pexels-photo-5410923.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    color: "bg-orange-500",
  },
  {
    icon: Train,
    name: "Rail",
    description: "Freight & Cargo Trains",
    image: "https://images.pexels.com/photos/5471182/pexels-photo-5471182.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    color: "bg-emerald-500",
  },
  {
    icon: Plane,
    name: "Air",
    description: "Air Cargo Services",
    image: "https://images.pexels.com/photos/358319/pexels-photo-358319.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    color: "bg-blue-500",
  },
  {
    icon: Ship,
    name: "Water",
    description: "Sea & River Shipping",
    image: "https://images.pexels.com/photos/35982637/pexels-photo-35982637.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    color: "bg-cyan-500",
  },
];

/**
 * Transport modes grid section
 */
export function TransportModes() {
  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="label-text mb-2">Transport Modes</p>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
            All Modes. One Platform.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
          {transportModes.map((mode) => (
            <Link 
              key={mode.name} 
              to={`/browse?mode=${mode.name.toLowerCase()}`}
              className="group relative aspect-[4/3] overflow-hidden bg-background"
              data-testid={`mode-${mode.name.toLowerCase()}`}
            >
              <img 
                src={mode.image} 
                alt={mode.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className={`w-12 h-12 ${mode.color} flex items-center justify-center mb-3`}>
                  <mode.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">{mode.name}</h3>
                <p className="text-sm text-white/70">{mode.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * How it works section
 */
export function HowItWorks() {
  const steps = [
    {
      step: "01",
      title: "Search Routes",
      description: "Find available cargo space on your desired route across India.",
      icon: MapPin,
    },
    {
      step: "02",
      title: "Choose Date",
      description: "Select departure date and time that suits your schedule.",
      icon: Calendar,
    },
    {
      step: "03",
      title: "Book Space",
      description: "Book instantly and connect directly with the logistics partner.",
      icon: Package,
    },
  ];

  return (
    <section className="py-20 bg-secondary/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="label-text mb-2">How It Works</p>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
            Simple. Fast. Reliable.
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((item) => (
            <div key={item.step} className="relative p-8 border border-border bg-background card-hover">
              <span className="text-6xl font-black text-secondary absolute top-4 right-4">
                {item.step}
              </span>
              <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                <item.icon className="w-6 h-6 text-primary-foreground" />
              </div>
              <h3 className="text-xl font-bold mb-2">{item.title}</h3>
              <p className="text-muted-foreground">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Local delivery CTA section
 */
export function LocalDeliveryCTA() {
  return (
    <section className="py-16 bg-accent/5 border-y border-accent/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="label-text text-accent mb-2">New Feature</p>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
              Inter-District Delivery
            </h2>
            <p className="text-muted-foreground mb-6">
              Fast local cargo delivery across districts. Door-to-door pickup with express and same-day options. Bikes, cars, and vans available.
            </p>
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex items-center gap-2 text-sm">
                <div className="w-8 h-8 bg-green-500 flex items-center justify-center">
                  <Package className="w-4 h-4 text-white" />
                </div>
                <span>Express 2-4 hrs</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-8 h-8 bg-blue-500 flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-white" />
                </div>
                <span>Multiple Stops</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-8 h-8 bg-orange-500 flex items-center justify-center">
                  <Truck className="w-4 h-4 text-white" />
                </div>
                <span>Up to 100kg</span>
              </div>
            </div>
            <Link to="/local-delivery">
              <Button size="lg" className="rounded-none btn-hover bg-accent text-accent-foreground hover:bg-accent/90">
                Book Local Delivery
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="relative aspect-video">
            <img 
              src="https://images.pexels.com/photos/4391470/pexels-photo-4391470.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Local delivery service"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Partner CTA section
 */
export function PartnerCTA() {
  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="relative aspect-video">
            <img 
              src="https://images.pexels.com/photos/4481259/pexels-photo-4481259.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Logistics partner"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <p className="label-text mb-2">For Partners</p>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
              Fill Your Empty Space
            </h2>
            <p className="text-muted-foreground mb-6">
              Monetize your vacant cargo space. List available capacity on trucks, trains, ships, or aircraft. Reach thousands of customers looking to ship.
            </p>
            <ul className="space-y-3 mb-6">
              {[
                "Free partner registration",
                "Real-time booking notifications",
                "Secure payment guarantee",
                "Dashboard analytics"
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <div className="w-5 h-5 bg-primary flex items-center justify-center">
                    <svg className="w-3 h-3 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <Link to="/auth?tab=register&role=partner">
              <Button size="lg" variant="outline" className="rounded-none btn-hover text-base px-8">
                <Users className="w-5 h-5 mr-2" />
                Become a Partner
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Enterprise Bulk Booking CTA section
 */
export function EnterpriseCTA() {
  return (
    <section className="py-20 bg-primary/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="order-2 md:order-1">
            <p className="label-text mb-2">For Enterprises</p>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">
              Bulk Booking Made Simple
            </h2>
            <p className="text-muted-foreground mb-6">
              Ship large volumes with ease. Upload your shipment list via CSV or use our bulk form. Get volume-based discounts up to 15% off.
            </p>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 bg-background border border-border">
                <Percent className="w-6 h-6 text-primary mb-2" />
                <p className="font-bold">Up to 15% Off</p>
                <p className="text-sm text-muted-foreground">Volume discounts</p>
              </div>
              <div className="p-4 bg-background border border-border">
                <Building2 className="w-6 h-6 text-primary mb-2" />
                <p className="font-bold">GST Invoices</p>
                <p className="text-sm text-muted-foreground">Corporate billing</p>
              </div>
            </div>
            <Link to="/bulk-booking">
              <Button size="lg" className="rounded-none btn-hover text-base px-8">
                <Package className="w-5 h-5 mr-2" />
                Start Bulk Booking
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="order-1 md:order-2 relative aspect-video">
            <img 
              src="https://images.pexels.com/photos/4483610/pexels-photo-4483610.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Enterprise logistics"
              className="w-full h-full object-cover"
            />
            <div className="absolute top-4 right-4 bg-primary text-primary-foreground px-4 py-2 font-bold">
              ENTERPRISE
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
